// services/openaiAPI.js
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

// Main function to mark student work
export const markStudentWork = async (studentFile, markScheme, userLevel, generateDownload = false, additionalContext = '') => {
  try {
    console.log('🔍 Starting OpenAI marking process...');
    console.log('File:', studentFile.name, 'Type:', studentFile.type, 'Size:', studentFile.size);

    // Step 1: Extract text from the file
    let studentText = '';
    
    if (studentFile.type === 'text/plain') {
      // Handle plain text files
      studentText = await readTextFile(studentFile);
    } else if (studentFile.type === 'application/pdf') {
      // Handle PDF files
      studentText = await extractTextFromPDF(studentFile);
    } else if (studentFile.type.startsWith('image/')) {
      // Handle image files (OCR would go here)
      throw new Error('Image OCR not implemented yet. Please convert images to PDF first.');
    } else {
      throw new Error(`Unsupported file type: ${studentFile.type}`);
    }

    console.log('📄 Extracted text length:', studentText.length);
    
    if (studentText.length < 10) {
      throw new Error('Could not extract meaningful text from the file. Please ensure the PDF contains selectable text or convert it to a text-based PDF.');
    }

    // Step 2: Get mark scheme text
    let markSchemeText = '';
    
    if (markScheme.fileURL && markScheme.fileURL.startsWith('data:text/plain;base64,')) {
      // Handle text input mark schemes
      markSchemeText = decodeURIComponent(escape(atob(markScheme.fileURL.split(',')[1])));
    } else if (markScheme.fileURL) {
      // Handle uploaded mark scheme files via server-side proxy (avoids CORS)
      try {
        const response = await fetch('/api/fetch-mark-scheme', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: markScheme.fileURL })
        });
        
        if (response.ok) {
          const blob = await response.blob();
          if (blob.type === 'application/pdf') {
            markSchemeText = await extractTextFromPDF(blob);
          } else {
            markSchemeText = await blob.text();
          }
        } else {
          throw new Error('Could not fetch mark scheme');
        }
      } catch (error) {
        console.error('Error fetching mark scheme:', error);
        throw new Error('Could not load mark scheme. Please try again or use text input.');
      }
    } else {
      throw new Error('No mark scheme provided');
    }

    console.log('📋 Mark scheme text length:', markSchemeText.length);

    // Step 3: Call OpenAI API via your endpoint
    const response = await fetch('/api/mark', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        markSchemeText,
        studentText,
        schemeName: markScheme.name,
        studentFileName: studentFile.name,
        userLevel,
        gradingMode: 'standard',
        fileType: studentFile.type,
        additionalContext
      })
    });

    console.log('📡 API response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`Marking failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ Marking completed successfully');

    // Step 4: Return standardized result
    return {
      ...result,
      schemeName: markScheme.name,
      studentFileName: studentFile.name
    };

  } catch (error) {
    console.error('❌ Error in markStudentWork:', error);
    throw error;
  }
};

// Helper function to read plain text files
const readTextFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('Failed to read text file'));
    reader.readAsText(file);
  });
};

// Helper function to extract text from PDF files
const extractTextFromPDF = async (file) => {
  try {
    console.log('📄 Starting PDF text extraction...');
    
    // Convert file to array buffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Load PDF document
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    console.log(`📄 PDF loaded: ${pdf.numPages} pages`);
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      console.log(`📄 Processing page ${pageNum}/${pdf.numPages}`);
      
      try {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Combine text items into readable text
        const pageText = textContent.items
          .map(item => item.str)
          .join(' ')
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
        
        if (pageText) {
          fullText += pageText + '\n\n';
        }
        
      } catch (pageError) {
        console.warn(`⚠️ Could not process page ${pageNum}:`, pageError.message);
        // Continue with other pages
      }
    }
    
    fullText = fullText.trim();
    
    if (fullText.length === 0) {
      throw new Error('No text found in PDF. The PDF might be image-based or corrupted. Please ensure the PDF contains selectable text.');
    }
    
    console.log(`✅ PDF text extraction complete: ${fullText.length} characters`);
    return fullText;
    
  } catch (error) {
    console.error('❌ PDF extraction failed:', error);
    
    if (error.message.includes('Invalid PDF')) {
      throw new Error('Invalid PDF file. Please ensure the file is not corrupted and try again.');
    } else if (error.message.includes('No text found')) {
      throw error; // Re-throw our custom message
    } else {
      throw new Error(`PDF processing failed: ${error.message}. Please try converting the PDF to a text-based format.`);
    }
  }
};

// Helper function to validate file before processing
export const validateFile = (file) => {
  const maxSize = 10 * 1024 * 1024; // 10MB limit
  const supportedTypes = [
    'application/pdf',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'image/webp'
  ];

  if (file.size > maxSize) {
    throw new Error('File too large. Maximum size is 10MB.');
  }

  if (!supportedTypes.includes(file.type)) {
    throw new Error(`Unsupported file type: ${file.type}. Supported types: PDF, plain text, and images.`);
  }

  return true;
};

// Export for testing/debugging
export const debugExtractText = async (file) => {
  try {
    validateFile(file);
    
    if (file.type === 'application/pdf') {
      return await extractTextFromPDF(file);
    } else if (file.type === 'text/plain') {
      return await readTextFile(file);
    } else {
      throw new Error('Debug extraction only supports PDF and text files');
    }
  } catch (error) {
    console.error('Debug extraction failed:', error);
    throw error;
  }
};